import { supabase } from "../../lib/supabase";
import { Event } from "./types";

class ScheduleService {
  // Calendar Methods
  async getEvents(userId: string): Promise<Event[]> {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', userId)
      .order('start_time', { ascending: true });

    if (error) throw error;
    return data.map(this.mapEventFromDB);
  }

  async createEvent(event: Omit<Event, 'id'>): Promise<Event> {
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([this.mapEventToDB(event)])
      .select()
      .single();

    if (error) throw error;
    return this.mapEventFromDB(data);
  }

  async updateEvent(event: Partial<Event> & { id: string }): Promise<Event> {
    const { data, error } = await supabase
      .from('calendar_events')
      .update(this.mapEventToDB(event))
      .eq('id', event.id)
      .select()
      .single();

    if (error) throw error;
    return this.mapEventFromDB(data);
  }

  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Helper Methods
  private mapEventFromDB(data: any): Event {
    return {
      id: data.id,
      userId: data.user_id,
      title: data.title,
      description: data.description,
      location: data.location,
      startTime: data.start_time,
      endTime: data.end_time,
      category: data.category,
      isAllDay: data.is_all_day,
      recurrence: data.recurrence,
      reminders: data.reminders ? JSON.parse(data.reminders) : undefined,
      createdAt: data.created_at ? new Date(data.created_at) : undefined,
      updatedAt: data.updated_at ? new Date(data.updated_at) : undefined
    };
  }

  private mapEventToDB(event: Partial<Event>): any {
    const dbEvent: any = {
      user_id: event.userId,
      title: event.title,
      description: event.description,
      location: event.location,
      start_time: event.startTime,
      end_time: event.endTime,
      category: event.category,
      is_all_day: event.isAllDay,
      recurrence: event.recurrence,
      reminders: event.reminders ? JSON.stringify(event.reminders) : null
    };

    // Remove undefined values
    Object.keys(dbEvent).forEach(key => {
      if (dbEvent[key] === undefined) {
        delete dbEvent[key];
      }
    });

    return dbEvent;
  }
}

export const scheduleService = new ScheduleService();
